// Notificaciones para la app del chofer: sonido, browser notif, vibración.

let audioCtx = null;
let _gestureBound = false;

function _bindGestureUnlock() {
  // Chrome/Safari suspend the AudioContext until a real user gesture. If the
  // dispatcher opens the app and the first trigger for a sound is our own
  // poll (no click yet), resume() silently fails and the whistle / ring is
  // never heard. Attach a one-shot listener that resumes the context on the
  // very first user interaction, then removes itself.
  if (_gestureBound || typeof document === "undefined") return;
  _gestureBound = true;
  const unlock = () => {
    try {
      if (audioCtx && audioCtx.state === "suspended") audioCtx.resume();
    } catch (_) { /* ignore */ }
  };
  ["pointerdown", "click", "keydown", "touchstart"].forEach((ev) =>
    document.addEventListener(ev, unlock, { once: false, passive: true, capture: true }),
  );
}

function getCtx() {
  if (audioCtx) return audioCtx;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  audioCtx = new AC();
  _bindGestureUnlock();
  return audioCtx;
}

// Uber-style "ding-ding" alert. Two short sine beeps a musical third apart
// (A5 → D6), with a fast attack + soft decay so it sounds like a bell rather
// than a siren. The pair is repeated 3× to fill ~1.6s, matching the 2 s poll
// loop in DriverApp for a near-continuous ring until the driver taps Aceptar.
export function playAlertSound() {
  const ctx = getCtx();
  if (!ctx) return;
  try {
    if (ctx.state === "suspended") ctx.resume();
    const now = ctx.currentTime;

    // One ding = one enveloped sine tone.
    const beep = (startAt, freq, dur = 0.16, gain = 0.99) => {
      const osc = ctx.createOscillator();
      const env = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      // Attack 5 ms, hold, then exponential decay to 0. Prevents click at end.
      env.gain.setValueAtTime(0, now + startAt);
      env.gain.linearRampToValueAtTime(gain, now + startAt + 0.005);
      env.gain.setValueAtTime(gain, now + startAt + dur * 0.55);
      env.gain.exponentialRampToValueAtTime(0.0001, now + startAt + dur);
      osc.connect(env);
      env.connect(ctx.destination);
      osc.start(now + startAt);
      osc.stop(now + startAt + dur + 0.02);
    };

    // Timeline: (ding lo · ding hi) × 3, with a small gap between pairs.
    // A5 = 880 Hz, D6 = 1174.66 Hz — same interval used by Uber's chime.
    const pattern = [0.00, 0.22, 0.55, 0.77, 1.10, 1.32];
    pattern.forEach((t, i) => beep(t, i % 2 === 0 ? 880 : 1174.66));
  } catch (err) {
    console.error("playAlertSound failed:", err);
  }
}

// Vibrate if supported (Android / most mobile browsers)
export function vibrateAlert() {
  if (navigator.vibrate) {
    try {
      navigator.vibrate([200, 100, 200, 100, 300]);
    } catch (err) {
      console.error("vibrateAlert failed:", err);
    }
  }
}

// Ask for Notification permission and show one
export async function requestNotificationPermission() {
  if (!("Notification" in window)) return "unsupported";
  if (Notification.permission === "granted") return "granted";
  if (Notification.permission === "denied") return "denied";
  const res = await Notification.requestPermission();
  return res;
}

// Church-bell "dong" for incoming trips whose passenger OWES the LLC — grave
// fundamental with a harmonic overtone, long decay. Grabs the dispatcher's
// attention because it stands out from the neutral polling silence.
export function playDebtChime() {
  const ctx = getCtx();
  if (!ctx) return;
  try {
    if (ctx.state === "suspended") ctx.resume();
    const now = ctx.currentTime;

    const dong = (startAt, freq, harmonic, dur, gain) => {
      // Fundamental
      const o1 = ctx.createOscillator(); const e1 = ctx.createGain();
      o1.type = "sine"; o1.frequency.value = freq;
      e1.gain.setValueAtTime(0, now + startAt);
      e1.gain.linearRampToValueAtTime(gain, now + startAt + 0.005);
      e1.gain.exponentialRampToValueAtTime(0.0001, now + startAt + dur);
      o1.connect(e1); e1.connect(ctx.destination);
      o1.start(now + startAt); o1.stop(now + startAt + dur + 0.03);
      // Harmonic — thinner, quieter, gives the "clang" character.
      const o2 = ctx.createOscillator(); const e2 = ctx.createGain();
      o2.type = "triangle"; o2.frequency.value = harmonic;
      e2.gain.setValueAtTime(0, now + startAt);
      e2.gain.linearRampToValueAtTime(gain * 0.4, now + startAt + 0.005);
      e2.gain.exponentialRampToValueAtTime(0.0001, now + startAt + dur * 0.6);
      o2.connect(e2); e2.connect(ctx.destination);
      o2.start(now + startAt); o2.stop(now + startAt + dur + 0.03);
    };

    // Two dongs: A3 (220 Hz) with A4 (440 Hz) harmonic, then a lower repeat.
    dong(0.00, 220, 440, 0.85, 0.85);
    dong(0.55, 196, 392, 0.85, 0.85);  // G3+G4 — slightly lower to feel "warning"
  } catch (err) { console.error("playDebtChime failed:", err); }
}

// Bright short "tinnn" for incoming trips whose passenger has CREDIT with the
// LLC — a happy A6 with a shimmering overtone, ~250 ms. Distinct from the
// grave debt bell so the dispatcher tells them apart without looking.
export function playCreditChime() {
  const ctx = getCtx();
  if (!ctx) return;
  try {
    if (ctx.state === "suspended") ctx.resume();
    const now = ctx.currentTime;
    const tinn = (startAt, freq, dur, gain) => {
      const o = ctx.createOscillator(); const e = ctx.createGain();
      o.type = "sine"; o.frequency.value = freq;
      e.gain.setValueAtTime(0, now + startAt);
      e.gain.linearRampToValueAtTime(gain, now + startAt + 0.003);
      e.gain.exponentialRampToValueAtTime(0.0001, now + startAt + dur);
      o.connect(e); e.connect(ctx.destination);
      o.start(now + startAt); o.stop(now + startAt + dur + 0.02);
    };
    // A6 (1760 Hz) + fifth-above overtone (E7 2637 Hz), 250 ms total.
    tinn(0.00, 1760, 0.25, 0.85);
    tinn(0.00, 2637, 0.25, 0.35);
  } catch (err) { console.error("playCreditChime failed:", err); }
}

// Special appointment chime — a 3-note ascending glass-bell pattern
// (C6 → E6 → G6) so the dispatcher instantly recognises a booking dropping
// into the panel vs a fresh call or a debt/credit ping.
export function playAppointmentChime() {
  const ctx = getCtx();
  if (!ctx) return;
  try {
    if (ctx.state === "suspended") ctx.resume();
    const now = ctx.currentTime;
    const bell = (startAt, freq, dur = 0.35, gain = 0.75) => {
      const o = ctx.createOscillator(); const e = ctx.createGain();
      o.type = "triangle"; o.frequency.value = freq;
      e.gain.setValueAtTime(0, now + startAt);
      e.gain.linearRampToValueAtTime(gain, now + startAt + 0.015);
      e.gain.exponentialRampToValueAtTime(0.0001, now + startAt + dur);
      o.connect(e); e.connect(ctx.destination);
      o.start(now + startAt); o.stop(now + startAt + dur + 0.03);
    };
    bell(0.00, 1046.50, 0.30, 0.60); // C6
    bell(0.18, 1318.51, 0.30, 0.60); // E6
    bell(0.36, 1567.98, 0.42, 0.75); // G6
  } catch (err) { console.error("playAppointmentChime failed:", err); }
}

// Ringtone pattern requested by the user: harmonic chord (880 + 1320 Hz) as
// two short 0.4s pulses separated by 0.1s of silence — that classic "trr-trr"
// incoming-call feel without looping. Peak gain 0.12 (soft) to avoid startling
// the dispatcher. Total pattern length ≈ 0.9 s.
export function playPhoneRing() {
  const ctx = getCtx();
  if (!ctx) return;
  try {
    if (ctx.state === "suspended") ctx.resume();
    const now = ctx.currentTime;

    const playTone = (startTime, duration) => {
      [880, 1320].forEach((freq) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(freq, startTime);
        // Soft envelope: 30 ms attack, exponential decay — no clicks.
        // Peak gain 0.24 (2× the previously halved 0.12) — restores the
        // original volume the user asked to bring back.
        gain.gain.setValueAtTime(0, startTime);
        gain.gain.linearRampToValueAtTime(0.24, startTime + 0.03);
        gain.gain.exponentialRampToValueAtTime(0.0001, startTime + duration);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(startTime);
        osc.stop(startTime + duration);
      });
    };

    playTone(now, 0.4);          // first pulse
    playTone(now + 0.5, 0.4);    // second pulse
  } catch (err) {
    console.error("playPhoneRing failed:", err);
  }
}

// Train whistle chord — plays when a driver taps "Terminé el viaje" and the
// trip transitions to `pending_review`. The dispatcher hears a distinctive
// short whistle so they know to approve a ride without eyeballing the panel.
// Ma-Bell-era steam whistle: A4 + C#5 + E5 + G5 triangle waves with a fast
// pitch bend into the note (the airflow hitting the reed) and a soft tail.
export function playTrainWhistle() {
  const ctx = getCtx();
  if (!ctx) return;
  try {
    if (ctx.state === "suspended") ctx.resume();
    const now = ctx.currentTime;
    const DURATION = 1.2; // seconds
    const frecs = [440, 554.37, 659.25, 783.99];
    frecs.forEach((frec) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "triangle";
      // Fast pitch bend simulating airflow entering the whistle reed.
      osc.frequency.setValueAtTime(frec * 0.95, now);
      osc.frequency.exponentialRampToValueAtTime(frec, now + 0.08);
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(0.1, now + 0.05);
      gain.gain.setValueAtTime(0.1, now + DURATION - 0.2);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + DURATION);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now);
      osc.stop(now + DURATION);
    });
  } catch (err) {
    console.error("playTrainWhistle failed:", err);
  }
}

export function showBrowserNotification(title, body) {
  if (!("Notification" in window)) return;
  if (Notification.permission !== "granted") return;
  try {
    const n = new Notification(title, {
      body,
      icon: "/favicon.ico",
      badge: "/favicon.ico",
      tag: "new-trip",
      renotify: true,
      requireInteraction: true,   // stays visible until user acts
      silent: false,
    });
    n.onclick = () => { window.focus(); n.close(); };
  } catch (err) {
    console.error("showBrowserNotification failed:", err);
  }
}
